import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Tab,
  Tabs,
  Card,
  IconButton,
  CircularProgress,
  Chip,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { Message } from '../models/Message';
import { PublicForm } from '../models/PublicForm';
import FirestoreRepository from '../services/FirestoreRepository';
import { doc, collection, addDoc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import SubmissionTracker from '../utils/SubmissionTracker';
import brand from '../theme/brand';
import { tok } from '../theme/businessTheme';
import { flattenQuestionTypes, isImageTypeHint, isStagedPhotoUrl } from '../utils/imageQuestions';
import { uploadFormImage } from '../services/ImageUploadService';
import { parseChoice, formatChoiceAnswer, matchDraftedChoice, ParsedChoice } from '../utils/choice';

interface QuestionnaireViewProps {
  message: Message;
  form: PublicForm;
  userId: string;
  onDismiss: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
      style={{ height: '100%', minHeight: 0 }}
    >
      {value === index && (
        <Box sx={{ p: 0.5, height: '100%', overflow: 'auto' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const QuestionnaireView: React.FC<QuestionnaireViewProps> = ({
  message,
  form,
  userId,
  onDismiss,
}) => {
  const theme = useTheme();
  const [answers, setAnswers] = useState<Record<string, Record<string, string>>>({});
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [purpose, setPurpose] = useState('');
  const [tabSections, setTabSections] = useState<string[]>(['Required']);

  // Image questions (spec §4d) — thumbnail + Retake, or "Add photo". Question
  // name -> type hint, from the form's own schema (not the drafted answers).
  const questionTypeMap = useMemo(() => flattenQuestionTypes(form.questions), [form.questions]);
  const isImageQuestion = (question: string) => isImageTypeHint(questionTypeMap[question]);

  // Choice questions (spec §5) — chips instead of a free-text field. Question
  // name -> parsed choice definition, from the form's own schema.
  const choiceQuestionMap = useMemo(() => {
    const map: Record<string, ParsedChoice> = {};
    Object.keys(questionTypeMap).forEach((question) => {
      const parsed = parseChoice(questionTypeMap[question]);
      if (parsed) map[question] = parsed;
    });
    return map;
  }, [questionTypeMap]);
  const isChoiceQuestion = (question: string) => !!choiceQuestionMap[question];

  // Selected options and "Other" free text per choice question, seeded from
  // the AI-drafted value (matched the same way tallying matches — §5).
  const [choiceSelections, setChoiceSelections] = useState<Record<string, string[]>>({});
  const [choiceOtherText, setChoiceOtherText] = useState<Record<string, string>>({});
  const [choiceErrors, setChoiceErrors] = useState<Record<string, string>>({});

  const toggleChoiceOption = (question: string, option: string) => {
    const choice = choiceQuestionMap[question];
    if (!choice) return;
    setChoiceErrors((prev) => ({ ...prev, [question]: '' }));
    setChoiceSelections((prev) => {
      const current = prev[question] || [];
      if (!choice.multi) {
        return { ...prev, [question]: current.includes(option) ? [] : [option] };
      }
      return {
        ...prev,
        [question]: current.includes(option)
          ? current.filter((o) => o !== option)
          : [...current, option],
      };
    });
  };

  const toggleChoiceOther = (question: string) => {
    const choice = choiceQuestionMap[question];
    if (!choice || !choice.allowOther) return;
    setChoiceErrors((prev) => ({ ...prev, [question]: '' }));
    setChoiceSelections((prev) => {
      const current = prev[question] || [];
      const isOtherOn = current.includes('__other__');
      if (isOtherOn) {
        return { ...prev, [question]: current.filter((o) => o !== '__other__') };
      }
      return {
        ...prev,
        [question]: choice.multi ? [...current, '__other__'] : ['__other__'],
      };
    });
  };

  /** Submitted value for a choice question given the current chip/Other
      state (spec §2). */
  const computeChoiceAnswer = (question: string): string => {
    const choice = choiceQuestionMap[question];
    if (!choice) return '';
    const selections = choiceSelections[question] || [];
    const selectedOptions = selections.filter((o) => o !== '__other__');
    const otherText = selections.includes('__other__') ? choiceOtherText[question] || '' : '';
    return formatChoiceAnswer(choice, selectedOptions, otherText);
  };

  const [stagedImages, setStagedImages] = useState<Record<string, string>>({});
  const [uploadingQuestion, setUploadingQuestion] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<string, string>>({});
  const pendingImageQuestionRef = useRef<string | null>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);

  const triggerImagePicker = (question: string) => {
    if (uploadingQuestion) return;
    pendingImageQuestionRef.current = question;
    imageFileInputRef.current?.click();
  };

  const handleImageFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const question = pendingImageQuestionRef.current;
    if (!file || !question) return;

    setUploadingQuestion(question);
    setImageErrors((prev) => ({ ...prev, [question]: '' }));
    try {
      const url = await uploadFormImage({ file, channelUid: form.channelUid, formId: form.id });
      setStagedImages((prev) => ({ ...prev, [question]: url }));
    } catch (error) {
      console.error('Error uploading review photo:', error);
      setImageErrors((prev) => ({ ...prev, [question]: 'Photo upload failed — try again.' }));
    } finally {
      setUploadingQuestion(null);
      pendingImageQuestionRef.current = null;
    }
  };

  // Initialize answers from message when component mounts
  useEffect(() => {
    try {
      const jsonObject = JSON.parse(message.messageText);
      const result: Record<string, Record<string, string>> = {};
      
      // Extract purpose if present
      if (jsonObject.Purpose) {
        setPurpose(jsonObject.Purpose);
      }
      
      // Extract sections
      if (jsonObject.Required && jsonObject.Optional) {
        // Handle structured format with Required/Optional
        result.Required = {};
        Object.keys(jsonObject.Required).forEach(key => {
          result.Required[key] = jsonObject.Required[key].toString();
        });
        
        result.Optional = {};
        Object.keys(jsonObject.Optional).forEach(key => {
          result.Optional[key] = jsonObject.Optional[key].toString();
        });
        
        setTabSections(['Required', 'Optional']);
      } else {
        // Handle other formats
        const isPurposeKey = (key: string) => key === 'Purpose';
        const nonPurposeKeys = Object.keys(jsonObject).filter(key => !isPurposeKey(key));
        
        if (nonPurposeKeys.every(key => typeof jsonObject[key] === 'object' && jsonObject[key] !== null)) {
          // It's a map of section names to question maps
          nonPurposeKeys.forEach(section => {
            result[section] = {};
            Object.keys(jsonObject[section]).forEach(key => {
              result[section][key] = jsonObject[section][key].toString();
            });
          });
          
          setTabSections(nonPurposeKeys);
        } else {
          // Flat structure - treat all as Required
          result.Required = {};
          nonPurposeKeys.forEach(key => {
            result.Required[key] = jsonObject[key].toString();
          });
          
          setTabSections(['Required']);
        }
      }
      
      setAnswers(result);

      // Seed staged images for any image question that was already given a
      // real Storage URL (e.g. attached earlier via the chat camera button).
      // A blank answer or the AI's literal "[photo attached]" placeholder
      // means no photo yet (spec §2, §4d).
      const seeded: Record<string, string> = {};
      Object.keys(result).forEach((section) => {
        Object.keys(result[section]).forEach((question) => {
          const value = result[section][question];
          if (isStagedPhotoUrl(value)) {
            seeded[question] = value;
          }
        });
      });
      if (Object.keys(seeded).length > 0) {
        setStagedImages((prev) => ({ ...seeded, ...prev }));
      }

      // Seed choice selections/Other text from the AI-drafted value using
      // the same tally matching as §2 (spec §5, "Pre-select from the
      // AI-drafted value").
      const seededSelections: Record<string, string[]> = {};
      const seededOther: Record<string, string> = {};
      Object.keys(result).forEach((section) => {
        Object.keys(result[section]).forEach((question) => {
          const choice = choiceQuestionMap[question];
          if (!choice) return;
          const matched = matchDraftedChoice(choice, result[section][question]);
          const selections = [...matched.selected];
          if (matched.otherText !== undefined) {
            selections.push('__other__');
            seededOther[question] = matched.otherText;
          }
          if (selections.length > 0) seededSelections[question] = selections;
        });
      });
      if (Object.keys(seededSelections).length > 0) {
        setChoiceSelections((prev) => ({ ...seededSelections, ...prev }));
      }
      if (Object.keys(seededOther).length > 0) {
        setChoiceOtherText((prev) => ({ ...seededOther, ...prev }));
      }
    } catch (error) {
      console.error('Error initializing answers:', error);
      // Set default empty structure
      setAnswers({ Required: {} });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.messageText, choiceQuestionMap]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabIndex(newValue);
  };

  const handleAnswerChange = (section: string, question: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [question]: value,
      },
    }));
  };

  const handleCancel = async () => {
    try {
      // Clear all messages in the conversation
      const db = getFirestore();
      const { collection, getDocs, writeBatch } = await import('firebase/firestore');

      const messagesRef = collection(
        db,
        'public_forms',
        form.id,
        'activities',
        'messages',
        userId
      );

      const snapshot = await getDocs(messagesRef);
      const batch = writeBatch(db);

      snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      console.log('Successfully cleared conversation on cancel');
    } catch (error) {
      console.error('Error clearing conversation:', error);
    }

    onDismiss();
  };

  const handleSubmit = async () => {
    // Image questions: the submitted answer is always the staged Storage URL
    // (never the AI's "[photo attached]" placeholder or raw text — spec §2).
    // A required image question with no staged photo blocks submit.
    const nextImageErrors: Record<string, string> = {};
    let firstMissingSection: string | null = null;
    Object.keys(answers).forEach((section) => {
      Object.keys(answers[section] || {}).forEach((question) => {
        if (!isImageQuestion(question)) return;
        if (!stagedImages[question]) {
          if (section === 'Required') {
            nextImageErrors[question] = 'Photo required';
            if (!firstMissingSection) firstMissingSection = section;
          }
        }
      });
    });
    // Choice questions: a required choice question with no valid selection
    // blocks submit, same as a missing required photo (spec §5). "Valid"
    // means at least one option chosen, or the Other chip on with non-blank
    // text.
    const nextChoiceErrors: Record<string, string> = {};
    Object.keys(answers).forEach((section) => {
      Object.keys(answers[section] || {}).forEach((question) => {
        if (!isChoiceQuestion(question)) return;
        if (section !== 'Required') return;
        const value = computeChoiceAnswer(question);
        if (!value) {
          nextChoiceErrors[question] = 'Please make a selection';
          if (!firstMissingSection) firstMissingSection = section;
        }
      });
    });
    if (Object.keys(nextChoiceErrors).length > 0) {
      setChoiceErrors((prev) => ({ ...prev, ...nextChoiceErrors }));
      if (firstMissingSection) {
        const idx = tabSections.indexOf(firstMissingSection);
        if (idx >= 0) setSelectedTabIndex(idx);
      }
      return;
    }

    if (Object.keys(nextImageErrors).length > 0) {
      setImageErrors((prev) => ({ ...prev, ...nextImageErrors }));
      if (firstMissingSection) {
        const idx = tabSections.indexOf(firstMissingSection);
        if (idx >= 0) setSelectedTabIndex(idx);
      }
      return;
    }

    try {
      // Convert answers to JSON, overriding image questions with the staged
      // Storage URL (or blank if optional and unanswered), and choice
      // questions with the chip-derived answer string (spec §2).
      const jsonObject: Record<string, any> = {
        Purpose: purpose,
      };

      Object.keys(answers).forEach((section) => {
        const sectionAnswers: Record<string, string> = { ...answers[section] };
        Object.keys(sectionAnswers).forEach((question) => {
          if (isImageQuestion(question)) {
            sectionAnswers[question] = stagedImages[question] || '';
          } else if (isChoiceQuestion(question)) {
            sectionAnswers[question] = computeChoiceAnswer(question);
          }
        });
        jsonObject[section] = sectionAnswers;
      });

      const answersJson = JSON.stringify(jsonObject);
      
      // Get Firestore instance
      const db = getFirestore();
      
      // Create submission message.
      // Forms audit P1-1: submissionId doubles as the idempotency key — rules
      // require submissionId == doc id and fromId == the auth uid, so a
      // double-submit targets the same doc and the second write is denied.
      const submissionData = {
        channelUid: form.channelUid,
        formQuestions: form.questions,
        fromId: userId,
        messageText: answersJson,
        toId: form.id,
        response: 'no',
        timestamp: Timestamp.now(),
        submissionId: message.id,
      };
      
      // Track that this response has been submitted
      SubmissionTracker.saveSubmittedResponse(message.id);
      
      // Add to reports collection using original message ID as document ID
      await setDoc(
        doc(
          db,
          'channels',
          form.channelUid,
          'public_forms',
          form.id,
          'reports',
          message.id  // Use original message ID as document ID
        ),
        submissionData
      );
      
      // Delete original message
      await deleteDoc(
        doc(
          db,
          'public_forms',
          form.id,
          'activities',
          'messages',
          userId,
          message.id
        )
      );
      
      // Create confirmation message with SAME ID as original message
      const confirmationData = {
        channelUid: form.channelUid,
        formQuestions: form.questions,
        fromId: form.id,
        messageText: answersJson,
        toId: userId,
        response: 'submitted',
        timestamp: Timestamp.now(),
      };
      
      // Save confirmation message using original message ID
      await setDoc(
        doc(
          db,
          'public_forms',
          form.id,
          'activities',
          'messages',
          userId,
          message.id  // Use same ID as original message
        ),
        confirmationData
      );

      // Mirror submission in global manager so AI listener sees it
      await setDoc(
        doc(
          db,
          'form_manager_public',
          message.id
        ),
        {
          ...confirmationData,
          fromId: userId,
          toId: form.id,
          response: 'submitted',
          submissionId: message.id,
        }
      );
      
      console.log('Questionnaire successfully submitted with ID:', message.id);
      onDismiss();
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      onDismiss();
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onDismiss}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: tok('surface'),
          color: tok('onSurface'),
          maxWidth: 680,
          width: '100%',
          mx: 'auto',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          px: 3, pt: 3, pb: 2.5,
          backgroundColor: tok('surface'),
        }}
      >
        <Box>
          <Typography
            variant="overline"
            component="div"
            sx={{ color: tok('onSurfaceMuted'), fontSize: '0.7rem', letterSpacing: '0.14em', lineHeight: 1.8 }}
          >
            REVIEW & SEND
          </Typography>
          <Typography variant="h5" component="div" sx={{ color: tok('onSurface'), fontSize: '2rem', lineHeight: 1.2, mt: 1 }}>
            Review your answers
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: tok('onSurfaceMuted') }}>{form.title}</Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: tok('onSurfaceMuted') }}>
            We've organized the details. Make any changes before sending.
          </Typography>
        </Box>

        <Button
          onClick={handleCancel}
          sx={{ borderRadius: 999, px: 2, minHeight: 44, color: tok('onSurfaceMuted') }}
        >
          Cancel
        </Button>
      </DialogTitle>
      
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1, minHeight: 0,
          px: 3, pb: 2,
        }}
      >
        <input
          ref={imageFileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleImageFileSelected}
        />

        {/* Section tabs */}
        {tabSections.length > 1 && (
          <Tabs
            value={selectedTabIndex}
            onChange={handleTabChange}
            aria-label="Questionnaire sections"
            sx={{ mb: 2, flexShrink: 0, '& .MuiTab-root': { color: tok('onSurfaceMuted'), textTransform: 'none' }, '& .Mui-selected': { color: `${tok('onSurface')} !important` }, '& .MuiTabs-indicator': { bgcolor: tok('tile') } }}
          >
            {tabSections.map((section, index) => (
              <Tab key={section} label={section} id={`tab-${index}`} />
            ))}
          </Tabs>
        )}
        
        {/* Questions list */}
        <Card
          sx={{
            flex: 1,
            mb: 2,
            bgcolor: 'transparent', boxShadow: 'none', border: 0,
            minHeight: 0, overflow: 'hidden',
          }}
        >
          {tabSections.map((section, index) => (
            <TabPanel key={section} value={selectedTabIndex} index={index}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {answers[section] && Object.keys(answers[section]).sort().map(question => (
                  <Box key={question} sx={{ p: 2.25, bgcolor: tok('card'), borderRadius: '20px', border: `1px solid ${tok('cardBorder')}` }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1.25, color: tok('onSurface') }}
                    >
                      {question}
                      {section === 'Required' && (
                        <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                          *
                        </Typography>
                      )}
                    </Typography>

                    {isChoiceQuestion(question) ? (
                      <Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {choiceQuestionMap[question].options.map((option) => {
                            const selected = (choiceSelections[question] || []).includes(option);
                            return (
                              <Chip
                                key={option}
                                label={option}
                                clickable
                                onClick={() => toggleChoiceOption(question, option)}
                                variant={selected ? 'filled' : 'outlined'}
                                sx={{
                                  borderRadius: '999px',
                                  fontWeight: 500,
                                  ...(selected
                                    ? {
                                        background: theme.palette.mode === 'light' ? brand.forestGradient : brand.sageGradient,
                                        color: brand.cream,
                                        border: '1px solid transparent',
                                      }
                                    : {
                                        borderColor: theme.palette.mode === 'light' ? brand.sage : brand.sageLight,
                                        color: theme.palette.mode === 'light' ? brand.sage : brand.sageLight,
                                      }),
                                }}
                              />
                            );
                          })}
                          {choiceQuestionMap[question].allowOther && (
                            <Chip
                              label="Other"
                              clickable
                              onClick={() => toggleChoiceOther(question)}
                              variant={(choiceSelections[question] || []).includes('__other__') ? 'filled' : 'outlined'}
                              sx={{
                                borderRadius: '999px',
                                fontWeight: 500,
                                ...((choiceSelections[question] || []).includes('__other__')
                                  ? {
                                      background: theme.palette.mode === 'light' ? brand.copperGradient : brand.copperGradient,
                                      color: '#FFFFFF',
                                      border: '1px solid transparent',
                                    }
                                  : {
                                      borderColor: brand.copper,
                                      color: brand.copper,
                                    }),
                              }}
                            />
                          )}
                        </Box>
                        {choiceQuestionMap[question].allowOther &&
                          (choiceSelections[question] || []).includes('__other__') && (
                            <TextField
                              fullWidth
                              placeholder="Tell us more…"
                              value={choiceOtherText[question] || ''}
                              onChange={(e) =>
                                setChoiceOtherText((prev) => ({ ...prev, [question]: e.target.value }))
                              }
                              variant="outlined"
                              size="small"
                              sx={{ mt: 1.25, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                            />
                          )}
                        {choiceErrors[question] && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                            {choiceErrors[question]}
                          </Typography>
                        )}
                      </Box>
                    ) : isImageQuestion(question) ? (
                      <Box>
                        {stagedImages[question] ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                              component="img"
                              src={stagedImages[question]}
                              alt={question}
                              sx={{
                                width: 120,
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                              }}
                            />
                            <Button
                              variant="outlined"
                              onClick={() => triggerImagePicker(question)}
                              disabled={uploadingQuestion === question}
                              startIcon={
                                uploadingQuestion === question ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <PhotoCameraIcon />
                                )
                              }
                              sx={{ borderRadius: 2, textTransform: 'none' }}
                            >
                              {uploadingQuestion === question ? 'Uploading…' : 'Retake'}
                            </Button>
                          </Box>
                        ) : (
                          <Button
                            variant="outlined"
                            onClick={() => triggerImagePicker(question)}
                            disabled={uploadingQuestion === question}
                            startIcon={
                              uploadingQuestion === question ? (
                                <CircularProgress size={16} />
                              ) : (
                                <PhotoCameraIcon />
                              )
                            }
                            sx={{ borderRadius: 2, textTransform: 'none' }}
                          >
                            {uploadingQuestion === question ? 'Uploading…' : 'Add photo'}
                          </Button>
                        )}
                        {imageErrors[question] && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                            {imageErrors[question]}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={8}
                        value={answers[section]?.[question] || ''}
                        onChange={(e) => handleAnswerChange(section, question, e.target.value)}
                        variant="outlined"
                        inputProps={{ 'aria-label': question }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px', bgcolor: tok('surface'), color: tok('onSurface'),
                          },
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </TabPanel>
          ))}
        </Card>
        
        {/* Submit button */}
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          disabled={!!uploadingQuestion}
          sx={{
            borderRadius: 99, flexShrink: 0,
            py: 1.75, minHeight: 56,
            fontSize: '1rem', textTransform: 'none',
            bgcolor: tok('tile'), color: tok('onTile'),
            '&:hover': { bgcolor: tok('tile'), filter: 'brightness(1.1)' },
          }}
        >
          Submit answers
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default QuestionnaireView;
